import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Spin,
  Tag,
  Modal,
  Button,
} from "antd";
import {
  CheckCircleTwoTone,
  CheckCircleOutlined,
  AppstoreTwoTone,
} from "@ant-design/icons";
import axios from "axios";
import ProductListCard from "../Product/ProductListCard";
import ProductDetail from "../Product/ProductDetail";
import { useParams, useLocation } from "react-router-dom";

const { Title } = Typography;

const NewArrival = ({ refreshCartCount }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showPrice, setShowPrice] = useState(!!localStorage.getItem("token"));
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const { categoryName } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mobileNumber = searchParams.get("mob");

  // Add a ref to track if mobile logging has been done
  const mobileLoggedRef = useRef(false);

  useEffect(() => {
    async function fetchNewArrivals() {
      setLoading(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL + "api/NewArrival/GetNewArrivals",
          {
            headers: {
              Authorization: "Bearer " + tokenString,
            },
          }
        );
        setData(response.data || []);
      } catch (error) {
        setData([]);
      } finally {
        setLoading(false);
      }
    }

    // Log mobile activity first if mobile number exists and hasn't been logged yet
    if (mobileNumber && !mobileLoggedRef.current) {
      logMobileActivity(mobileNumber);
      mobileLoggedRef.current = true;
    }

    fetchNewArrivals();
    logUserActivity();
  }, []); // Empty dependency array - runs only once on mount

  // Reset the ref when component unmounts
  useEffect(() => {
    return () => {
      mobileLoggedRef.current = false;
    };
  }, []);

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      const jsondata = JSON.stringify({
        ScreenID: "1004",
        DetailID: "0",
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // Add this helper function to clean mobile number
  const cleanMobileNumber = (mobileNo) => {
    if (!mobileNo) return null;

    // Remove all non-digit characters
    let cleaned = mobileNo.replace(/\D/g, "");

    // Remove leading +91 or 91
    if (cleaned.startsWith("91") && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }

    // Check if it's exactly 10 digits
    if (cleaned.length === 10) {
      return cleaned;
    }

    return null; // Invalid mobile number
  };

  // Update the logMobileActivity function
  const logMobileActivity = async (mobileNo) => {
    try {
      const cleanedMobile = cleanMobileNumber(mobileNo);

      if (!cleanedMobile) {
        console.error("Invalid mobile number format:", mobileNo);
        return;
      }
      console.log(cleanedMobile);
      const config = {
        method: "post",
        url: `${global.config.APIURL.BaseURL}api/PublicData/LogURL?MobileNo=${cleanedMobile}&ScreenID=1004`,
        headers: {
          "Content-Type": "application/json",
        },
      };

      await axios.request(config);
    } catch (error) {
      console.error("Error logging mobile activity:", error);
    }
  };

  // All category names for checkbox group
  const [allCategoryNames, setAllCategoryNames] = useState([]);

  // Set selected category from URL param after data is loaded
  useEffect(() => {
    if (data.length > 0) {
      const allCategoryNames = data.map((item) => item.Category);
      setAllCategoryNames(allCategoryNames);

      // Handle category from URL parameter
      if (categoryName) {
        // Decode URL-encoded category name and clean it
        let decodedCategoryName = decodeURIComponent(categoryName);

        // Handle malformed URL where & is used instead of ?
        if (decodedCategoryName.includes("&mob=")) {
          decodedCategoryName = decodedCategoryName.split("&mob=")[0];
        }

        console.log("Decoded category name:", decodedCategoryName);

        // Check if the decoded category exists in the data (case-insensitive)
        const categoryExists = allCategoryNames.find(
          (cat) => cat.toLowerCase() === decodedCategoryName.toLowerCase()
        );

        if (categoryExists) {
          console.log("Category found, selecting:", categoryExists);
          setSelectedCategories([categoryExists]);
        } else {
          console.log("Category not found, selecting all categories");
          console.log("Available categories:", allCategoryNames);
          setSelectedCategories(allCategoryNames);
        }
      } else {
        // If no category in URL, select all categories
        setSelectedCategories(allCategoryNames);
      }
    }
  }, [data, categoryName]);

  // Update the mobile number extraction to handle both URL formats
  const extractMobileFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    let mobileFromQuery = searchParams.get("mob");

    // If not found in query params, check if it's in the path (malformed URL)
    if (!mobileFromQuery && categoryName && categoryName.includes("&mob=")) {
      const parts = categoryName.split("&mob=");
      mobileFromQuery = parts[1];
    }

    return mobileFromQuery;
  };

  // Update the mobile logging logic
  useEffect(() => {
    const mobileNumber = extractMobileFromUrl();

    if (mobileNumber && !mobileLoggedRef.current) {
      logMobileActivity(mobileNumber);
      mobileLoggedRef.current = true;
    }
  }, [categoryName, location.search]);

  // Handle checkbox change
  const handleCategoryChange = (checkedValues) => {
    if (checkedValues.includes("All")) {
      setSelectedCategories(allCategoryNames);
    } else {
      setSelectedCategories(checkedValues);
    }
  };

  // Handle "All" checkbox separately
  const isAllChecked =
    selectedCategories.length === allCategoryNames.length &&
    allCategoryNames.length > 0;

  // Filtered categories to display:
  const filteredData =
    selectedCategories.length === allCategoryNames.length
      ? allCategoryNames
          .map((catName) => data.find((cat) => cat.Category === catName))
          .filter(Boolean)
      : [...selectedCategories]
          .reverse()
          .map((catName) => data.find((cat) => cat.Category === catName))
          .filter(Boolean);

  useEffect(() => {
    const updateShowPrice = () => setShowPrice(!!localStorage.getItem("token"));
    updateShowPrice();
    window.addEventListener("storage", updateShowPrice);
    return () => window.removeEventListener("storage", updateShowPrice);
  }, []);

  // Handler for product card click
  const handleProductCardClick = (product) => {
    setSelectedProductId(product.ProductID);
    setShowProductModal(true);
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 8 }}>
      <Card
        style={{
          borderRadius: 16,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          background: "#fff",
          margin: 0,
        }}
        styles={{ padding: 8 }}
      >
        <Title
          level={2}
          style={{ textAlign: "center", marginBottom: 16, marginTop: 16 }}
        >
          ✨ New Arrivals ✨
        </Title>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: "center", color: "#888", fontSize: 18 }}>
            No new arrivals found.
          </div>
        ) : (
          <Row gutter={16}>
            {/* Left column: Category checkboxes */}
            <Col xs={24} sm={6} md={5} lg={5} style={{ minWidth: 150 }}>
              <Card
                style={{
                  marginBottom: 0,
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                  background: "#fafafa",
                }}
                styles={{ padding: 16 }}
              >
                <Title level={4} style={{ marginBottom: 12, fontSize: 18 }}>
                  Categories
                </Title>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  {/* "All" Tag */}
                  <Tag.CheckableTag
                    checked={isAllChecked}
                    onChange={(checked) => {
                      if (checked) {
                        setSelectedCategories(allCategoryNames);
                      } else {
                        setSelectedCategories([]);
                      }
                    }}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 20,
                      fontWeight: 500,
                      fontSize: 16,
                      background: isAllChecked ? "#1677ff" : "#f0f5ff",
                      color: isAllChecked ? "#fff" : "#1677ff",
                      border: isAllChecked
                        ? "1.5px solid #1677ff"
                        : "1.5px solid #d6e4ff",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <AppstoreTwoTone
                      twoToneColor={isAllChecked ? "#fff" : "#1677ff"}
                      style={{ fontSize: 18 }}
                    />
                    All
                  </Tag.CheckableTag>
                  {/* Category Tags */}
                  {allCategoryNames.map((cat) => (
                    <Tag.CheckableTag
                      key={cat}
                      checked={selectedCategories.includes(cat)}
                      onChange={(checked) => {
                        if (checked) {
                          setSelectedCategories([...selectedCategories, cat]);
                        } else {
                          setSelectedCategories(
                            selectedCategories.filter((c) => c !== cat)
                          );
                        }
                      }}
                      style={{
                        padding: "8px 16px",
                        borderRadius: 20,
                        fontWeight: 500,
                        fontSize: 13,
                        background: selectedCategories.includes(cat)
                          ? "#1677ff"
                          : "#f0f5ff",
                        color: selectedCategories.includes(cat)
                          ? "#fff"
                          : "#1677ff",
                        border: selectedCategories.includes(cat)
                          ? "1.5px solid #1677ff"
                          : "1.5px solid #d6e4ff",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {selectedCategories.includes(cat) && (
                        <CheckCircleOutlined
                          twoToneColor="#fff"
                          style={{ fontSize: 18 }}
                        />
                      )}
                      {cat}
                    </Tag.CheckableTag>
                  ))}
                </div>
              </Card>
            </Col>
            {/* Right column: Products */}
            <Col xs={24} sm={18} md={19} lg={19}>
              {filteredData.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#888",
                    fontSize: 18,
                    marginTop: 40,
                  }}
                >
                  No categories selected.
                </div>
              ) : (
                filteredData.map((category) => (
                  <div key={category.Category} style={{ marginBottom: 16 }}>
                    <Divider
                      orientation="left"
                      style={{
                        margin: 0,
                        marginBottom: 8,
                        borderColor: "#1677ff",
                      }}
                    >
                      <Title level={3} style={{ margin: 0 }}>
                        {category.Category}
                      </Title>
                    </Divider>
                    <Row gutter={[16, 16]} style={{ margin: 0 }}>
                      {category.Products.map((product) => (
                        <Col
                          xs={12}
                          sm={12}
                          md={8}
                          lg={6}
                          key={product.ProductID}
                          style={{ padding: 8 }}
                        >
                          <ProductListCard
                            product={product}
                            showProdStatus
                            showPrice={showPrice}
                            onClick={handleProductCardClick}
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))
              )}
            </Col>
          </Row>
        )}
      </Card>
      {/* Product Detail Modal */}
      <Modal
        open={showProductModal}
        onCancel={() => {
          setShowProductModal(false);
          setSelectedProductId(null);
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "16px 0 0 0",
            }}
          >
            <Button
              type="primary"
              style={{
                minWidth: 120,
                borderRadius: 6,
                background: "#ff4d4f",
                borderColor: "#ff4d4f",
              }}
              onClick={() => {
                setShowProductModal(false);
                setSelectedProductId(null);
              }}
            >
              Close
            </Button>
          </div>
        }
        width="90%"
        style={{ maxWidth: 900 }}
        destroyOnClose
        styles={{ padding: 0 }}
      >
        {showProductModal && selectedProductId && (
          <ProductDetail
            refreshCartCount={refreshCartCount}
            productIdQuery={selectedProductId}
            isModal
            setDocumentTitle={() => {}}
          />
        )}
      </Modal>
    </div>
  );
};

export default NewArrival;
